import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const CATS = ["All", "Groceries", "Meats", "Condiments", "Frozen Products", "Sunjin Feeds"];
const MEAT_SUBS = ["All", "Pork", "Chicken", "Beef"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "All";
  const initialSub = params.get("subcategory") || "All";
  const [category, setCategory] = useState(initialCat);
  const [subcategory, setSubcategory] = useState(initialSub);
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCategory(params.get("category") || "All");
    setSubcategory(params.get("subcategory") || "All");
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category !== "All") p.category = category;
    if (category === "Meats" && subcategory !== "All") p.subcategory = subcategory;
    if (q) p.q = q;
    api.get("/products", { params: p }).then((r) => {
      // When viewing "All" products, surface fresh meats at the top
      let items = r.data;
      if (category === "All") {
        const order = { "Meats": 0, "Groceries": 1, "Condiments": 2, "Frozen Products": 3, "Sunjin Feeds": 4 };
        items = [...items].sort((a, b) => (order[a.category] ?? 99) - (order[b.category] ?? 99));
      }
      setProducts(items);
    }).finally(() => setLoading(false));
  }, [category, subcategory, q]);

  const selectCategory = (c) => {
    setCategory(c);
    setSubcategory("All");
    if (c === "All") setParams({}); else setParams({ category: c });
  };

  const selectSub = (s) => {
    setSubcategory(s);
    const next = { category };
    if (s !== "All") next.subcategory = s;
    setParams(next);
  };

  const heading = useMemo(() => {
    if (category === "All") return "All products";
    if (category === "Meats" && subcategory !== "All") return `${subcategory} · Meats`;
    return category;
  }, [category, subcategory]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 page-enter">
      <SEO
        title={`${heading} — Shop Online | Arbe Minimart`}
        description={`Shop ${heading.toLowerCase()} online at Arbe Minimart. Fresh, local, delivered same-day in Oroquieta City, Misamis Occidental. FREE delivery over ₱1,000.`}
        path={`/shop${category !== "All" ? `?category=${encodeURIComponent(category)}` : ""}`}
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div>
          <div className="overline">Shop</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1C2826] tracking-tighter">{heading}</h1>
          <p className="text-[#4A5D5A] mt-2 text-sm sm:text-base">Browse and add items to your cart.</p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#738380]" />
          <Input
            placeholder="Search products..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 h-11 rounded-full border-[#E1E5DF] bg-white"
            data-testid="shop-search"
          />
        </div>
      </div>

      <div className="flex flex-nowrap overflow-x-auto scrollbar-none gap-2 mb-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {CATS.map((c) => (
          <Button
            key={c}
            variant={category === c ? "default" : "outline"}
            size="sm"
            onClick={() => selectCategory(c)}
            className={`rounded-full px-4 h-9 flex-shrink-0 ${category === c ? "bg-[#2F5233] hover:bg-[#1F3D24] text-white" : "border-[#E1E5DF] text-[#1C2826] hover:bg-[#F1F3EE]"}`}
            data-testid={`filter-${c.toLowerCase().replace(/ /g, "-")}`}
          >
            {c}
          </Button>
        ))}
      </div>

      {category === "Meats" && (
        <div className="flex flex-nowrap overflow-x-auto scrollbar-none gap-2 mb-8 pl-1 border-l-2 border-[#C62828]/40 ml-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C62828] self-center ml-2 mr-1 flex-shrink-0">Meat type</span>
          {MEAT_SUBS.map((s) => (
            <Button
              key={s}
              variant={subcategory === s ? "default" : "outline"}
              size="sm"
              onClick={() => selectSub(s)}
              className={`rounded-full px-4 h-8 flex-shrink-0 ${subcategory === s ? "bg-[#C62828] hover:bg-[#8E1E1E] text-white" : "border-[#E1E5DF] text-[#1C2826] hover:bg-[#F1F3EE]"}`}
              data-testid={`filter-sub-${s.toLowerCase()}`}
            >
              {s}
            </Button>
          ))}
        </div>
      )}
      {category !== "Meats" && <div className="mb-4" />}

      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="h-10 w-10 border-4 border-[#2F5233] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="py-20 text-center text-[#738380]" data-testid="empty-products">No products found.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="products-grid">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
